import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppScreen, PageIntro, Section, SectionHeader, SwitchList, SwitchRow } from 'components/ds';
import { NotificationRepository, NotificationSettingsData } from 'repository/notificationRepository';
import styles from './Notifications.module.css';

export const Notifications: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(true);
    const [meetingReminder, setMeetingReminder] = useState(true);
    const [chatNotification, setChatNotification] = useState(true);
    const [scheduleNotification, setScheduleNotification] = useState(true);
    const [costNotification, setCostNotification] = useState(true);

    const initialized = useRef(false);

    // 초기 로드: DOKOCREW 알림 설정 조회 (pushEnabled 포함)
    // wiz-sso msg-consent API가 게이트웨이에 준비되면 별도 조회로 분리 가능
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await NotificationRepository.onGetSettings();
                if (response?.data?.data) {
                    const data = response.data.data;
                    setPushEnabled(data.pushEnabled ?? true);
                    setMeetingReminder(data.meetingReminder ?? true);
                    setChatNotification(data.chatNotification ?? true);
                    setScheduleNotification(data.scheduleNotification ?? true);
                    setCostNotification(data.costNotification ?? true);
                }
            } catch (error) {
                console.error('알림 설정 조회 실패:', error);
            } finally {
                setLoading(false);
                setTimeout(() => { initialized.current = true; }, 0);
            }
        };
        fetchSettings();
    }, []);

    // 세부 항목 변경 시 전체 푸시 상태 동기화
    // - 모두 꺼지면 전체 푸시도 off
    // - 하나라도 켜지면 전체 푸시도 on
    useEffect(() => {
        if (!initialized.current) return;
        const anyOn = meetingReminder || chatNotification || scheduleNotification || costNotification;
        if (pushEnabled !== anyOn) {
            setPushEnabled(anyOn);
        }
    }, [meetingReminder, chatNotification, scheduleNotification, costNotification]); // eslint-disable-line react-hooks/exhaustive-deps

    // 설정 변경 시 자동 저장 (디바운스)
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const saveSettings = useCallback(async (data: NotificationSettingsData) => {
        setSaving(true);
        try {
            await NotificationRepository.onSaveSettings(data);
            // TODO: wiz-sso msg-consent API가 게이트웨이에 준비되면 pushEnabled 변경 시 별도 호출
            // await NotificationRepository.onUpdatePushConsent(data.pushEnabled);
        } catch (error) {
            console.error('알림 설정 저장 실패:', error);
        } finally {
            setSaving(false);
        }
    }, []);

    useEffect(() => {
        if (!initialized.current) return;

        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            saveSettings({
                pushEnabled,
                meetingReminder,
                chatNotification,
                scheduleNotification,
                costNotification,
            });
        }, 500);

        return () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
        };
    }, [pushEnabled, meetingReminder, chatNotification, scheduleNotification, costNotification, saveSettings]);

    if (loading) {
        return (
            <div className="notifications-wrapper">
                <div className="notifications-header">
                    <button className="back-button" onClick={() => navigate('/settings')}>
                        ←
                    </button>
                    <h1 className="notifications-title">{t('notification.title')}</h1>
                </div>
                <div className="notifications-loading">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <AppScreen
            title={t('notification.title')}
            bar="sub"
            onBack={() => navigate('/settings')}
            right={saving ? <span className={styles.saving}>{t('common.save')}</span> : undefined}
        >
            <PageIntro title={t('notification.manage')} desc={t('notification.manageDesc')} />

            {/* 전체 푸시 — 이걸 끄면 아래가 모두 꺼집니다 */}
            <Section spacing="tight">
                <SwitchList>
                    <SwitchRow
                        label={t('notification.pushAll')}
                        desc={t('notification.pushAllDesc')}
                        checked={pushEnabled}
                        onChange={(enabled) => {
                            setPushEnabled(enabled);
                            setMeetingReminder(enabled);
                            setChatNotification(enabled);
                            setScheduleNotification(enabled);
                            setCostNotification(enabled);
                        }}
                    />
                </SwitchList>
            </Section>

            {/* 항목별 */}
            <Section divided last>
                <SectionHeader title={t('notification.sectionEach')} />
                <SwitchList>
                    <SwitchRow
                        label={t('notification.meetingReminder')}
                        desc={t('notification.meetingReminderDesc')}
                        checked={meetingReminder}
                        disabled={!pushEnabled}
                        onChange={setMeetingReminder}
                    />
                    <SwitchRow
                        label={t('notification.chatNotification')}
                        desc={t('notification.chatNotificationDesc')}
                        checked={chatNotification}
                        disabled={!pushEnabled}
                        onChange={setChatNotification}
                    />
                    <SwitchRow
                        label={t('notification.scheduleNotification')}
                        desc={t('notification.scheduleNotificationDesc')}
                        checked={scheduleNotification}
                        disabled={!pushEnabled}
                        onChange={setScheduleNotification}
                    />
                    <SwitchRow
                        label={t('notification.costNotification')}
                        desc={t('notification.costNotificationDesc')}
                        checked={costNotification}
                        disabled={!pushEnabled}
                        onChange={setCostNotification}
                    />
                </SwitchList>
            </Section>
        </AppScreen>
    );
};
